// Status indicator showing recording state and progress

import type { RecordingStatus as Status } from '@/lib/store'

interface RecordingStatusProps {
  status: Status
  chunksUploaded: number
  duration: number
  error: string | null
  onRetry?: () => void
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function RecordingStatus({
  status,
  chunksUploaded,
  duration,
  error,
  onRetry,
}: RecordingStatusProps) {
  const renderContent = () => {
    switch (status) {
      case 'idle':
        return (
          <span className="text-gray-400">
            Tap record to start
          </span>
        )

      case 'initializing':
        return (
          <span className="text-yellow-400 flex items-center gap-2">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
            Initializing camera...
          </span>
        )

      case 'ready':
        return (
          <span className="text-green-400 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full" />
            Ready to record
          </span>
        )

      case 'recording':
        return (
          <div className="flex items-center gap-4">
            <span className="text-red-400 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Recording
            </span>
            <span className="text-white font-mono">
              {formatDuration(duration)}
            </span>
            <span className="text-gray-400 text-sm">
              {chunksUploaded} chunks saved
            </span>
          </div>
        )

      case 'uploading':
        return (
          <span className="text-yellow-400 flex items-center gap-2">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
            Finalizing upload...
          </span>
        )

      case 'complete':
        return (
          <span className="text-green-400 flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Video saved! Under review.
          </span>
        )

      case 'error':
        return (
          <div className="flex items-center gap-3">
            <span className="text-red-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error || 'An error occurred'}
            </span>
            {onRetry && (
              <button
                onClick={onRetry}
                className="text-sm px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="text-sm py-2">
      {renderContent()}
    </div>
  )
}
